import styled, { css } from 'styled-components'

import media from 'utils/media'

export const StyledPostTitle = styled.h4`
   margin-bottom: 16px;
   display: flex;

   span:first-of-type {
      opacity: 0;
      color: ${({ theme }) => theme.colors.hotCinnamon};
      transition: all 0.55s cubic-bezier(0, 0.89, 0.44, 1) 0s;
      display: inline-block;
      margin-right: 8px;
      font-family: "Arial";
      font-weight: 700;
   }

   span:last-of-type {
      display: inline-block;
      transform: translateX(-15px);
      transition: all 0.55s cubic-bezier(0, 0.89, 0.44, 1) 0s;
   }
`

export const StyledReadMore = styled.button`
   background-color: transparent;
   border: none;
   display: flex;
   align-items: center;
   padding: 0;
   margin-top: 8px;

   span {
      display: block;
      margin: 0 8px 0 0;
      font-size: 12px;
      font-weight: 300;
      line-height: 1;
   }

   svg {
      font-size: 10px;
      opacity: 1;
      transition: all 0.55s cubic-bezier(0, 0.89, 0.44, 1) 0s;

      ${media.greaterThan('md')`
         opacity: 0;
      `}
   }
`

export const StyledPostInfo = styled.div`
   margin-bottom: 16px;
   display: ${({ $mobile }) => ($mobile ? 'block' : 'none')};

   ${media.greaterThan('md')`
      grid-column: 1;
      grid-row: 2;
      margin-bottom: 0;

      display: ${({ $mobile, $isFeatured }) =>
         $isFeatured && !$mobile ? 'block' : !$isFeatured && $mobile ? 'block' : 'none'};
   `}
`

export const StyledPost = styled.div`
   padding-bottom: 32px;
   cursor: pointer;
   border-bottom: 0.5px solid;
   border-color: ${({ theme }) => (theme.darkMode ? theme.colors.grey_300 : theme.colors.accent_300)};

   &:not(:last-of-type) {
      margin-bottom: 32px;
   }

   ${media.greaterThan('md')`
      padding-bottom: 34px;
      display: ${({ $isFeatured }) => ($isFeatured ? 'flex' : 'grid')};
      flex-direction: ${({ $isFeatured }) => ($isFeatured ? 'column' : 'row')};
      grid-template-columns: 1fr 1fr;
      grid-template-rows: auto auto;
      gap: 0 16px;
      align-items: start;

      ${({ $widerDescription }) =>
         $widerDescription &&
         `
         grid-template-columns: 1fr 1.2fr;
      `}

      &:not(:last-of-type) {
         margin-bottom: 34px;
      }

      &:hover {
         ${StyledPostTitle} {
            span:first-of-type {
               opacity: 1;
            }

            span:last-of-type {
               transform: translateX(0);
            }
         }

         ${StyledReadMore} {
            svg {
               opacity: 1;
            }
         }
      }

      ${({ $isFeatured }) =>
         $isFeatured
            ? css`
                 ${StyledPostInfo} {
                    order: 2;
                 }

                 ${StyledReadMore} {
                    margin-bottom: 24px;
                 }

                 ${CategoriesList} {
                    margin-bottom: 12px;
                 }

                 ${StyledPostTitle} {
                    margin-bottom: 12px;
                    font-size: 36px;
                    line-height: 1;
                 }

                 ${StyledExcerpt} {
                    font-size: 16px;
                    line-height: 29px;
                 }
              `
            : ''}
   `};

   ${media.greaterThan('xl')`
      gap: 0 50px;
   `}
`

export const StyledLeft = styled.div`
   ${media.greaterThan('md')`
      grid-column: 1;
      grid-row: 1 / span 2;
   `}
`

export const StyledRight = styled.div`
   ${media.greaterThan('md')`
      grid-column: 2;
      grid-row: 1 / span 2;
      padding-right: 16px;
   `}

   ${media.greaterThan('xl')`
      padding-right: 24px;
   `}
`

export const CategoriesList = styled.ul`
   margin-bottom: 16px;
   display: flex;
   align-items: center;
   gap: 8px;

   ${media.greaterThan('md')`
      grid-column: 1;
      grid-row: 1;
   `}
`

export const CategoryListEl = styled.li`
   margin: 0;
`

export const Category = styled.button`
   height: 25px;
   margin: 0;
   padding: 0px 12px;
   cursor: pointer;
   background-color: ${({ theme }) => (theme.darkMode ? theme.colors.hotCinnamon : theme.colors.accent_120)};
   border: none;
   border-radius: 4px;
   font-size: 12px;
   line-height: 22px;
   letter-spacing: 0.02em;
   font-weight: 400;
   color: ${({ theme }) => (theme.darkMode ? theme.colors.white : theme.colors.accent)};
   text-transform: capitalize;
`

export const StyledAuthors = styled.div`
   display: flex;
   align-items: center;
   margin-bottom: 8px;

   & > div:first-of-type {
      display: flex;
      align-items: center;
   }
`

export const StyledAuthorImagesContainer = styled.div`
   display: flex;
   align-items: center;
   margin-right: ${({ $totalAuthors }) => $totalAuthors > 1 ? '0px' : '8px'};  // Add positive margin to offset the negative transform
`

export const StyledAuthorImg = styled.div`
   width: 24px;
   height: 24px;
   border-radius: 50%;
   overflow: hidden;
   background-color: ${({ theme }) => theme.colors.textColor};
   transform: ${({ n }) => `translateX(${n * -8}px)`};  // 24px * 0.33 = 8px overlap
   position: relative;
   z-index: ${({ n }) => 5 + n};
`

export const StyledAuthorName = styled.div`
   font-weight: 500;
   font-size: 18px;
   line-height: 29px;
   font-family: ${({ theme }) => theme.fonts.portrait};
   strong {
      font-weight: 700;
      font-family: "Arial";
      a:hover {
         color: ${({ theme }) => theme.colors.hotCinnamon};
      }
   }
`

export const StyledDate = styled.span`
   line-height: 1;
   font-weight: ${({ $isFeatured }) => ($isFeatured ? '400' : '300')};
   display: block;
   font-size: 12px;
   line-height: 19px;
`

export const StyledExcerpt = styled.p`
   font-size: 14px;
   line-height: 25px;
   letter-spacing: 0.02em;
   margin-bottom: 0;
`

